import React, { useState } from 'react';
import { Drawer, Button, Select, Input } from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../../theme/utils';
import { Tooltip, Upload } from 'antd';
import { apiUploadCsvWithMapping } from '../../api';
import { CsvMappingDto } from '../../../../models/ContactImportProcess';
import { useNavigate } from 'react-router-dom';

interface NewImportDrawerProps {
  visible: boolean;
  onClose: () => void;
}

const useStyles = makePrioStyles((theme) => ({
  drawer: {
    zIndex: 999,
    padding: theme.spacing.regular,
  },
  uploadButton: {
    marginBottom: theme.spacing.large,
  },
  selectContainer: {
    marginTop: theme.spacing.regular,
  },
  actionButton: {
    marginLeft: theme.spacing.regular,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.large,
  },
  inputContainer: {
    marginBottom: theme.spacing.large,
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: theme.spacing.large,
  },
}));

// Mapping field → translated label
const CSV_MAPPING_LABELS: Record<string, string> = {
  // Company
  companyLegalName: 'Firma — Firmenname',
  companyDisplayName: 'Firma — Kurzname',
  companyNotes: 'Firma — Notizen',
  companyCommercialRegisterNumber: 'Firma — Handelsregisternr.',
  companyCommercialRegisterCity: 'Firma — Handelsregisterstadt',
  companyLegalForm: 'Firma — Rechtsform',
  companyVatNumber: 'Firma — USt-IdNr.',
  // Bank
  bankCreditInstitutionName: 'Bank — Kreditinstitut',
  bankBic: 'Bank — BIC',
  bankIban: 'Bank — IBAN',
  // BusinessUnit
  businessUnitDisplayName: 'Standort — Name',
  businessUnitPhone: 'Standort — Telefon',
  businessUnitEMail: 'Standort — E-Mail',
  businessUnitStreet: 'Standort — Straße',
  businessUnitZip: 'Standort — PLZ',
  businessUnitCity: 'Standort — Ort',
  businessUnitCountry: 'Standort — Land',
  businessUnitRegion: 'Standort — Region',
  businessUnitIsPrimary: 'Standort — Primär',
  // Contact
  contactSalutation: 'Kontakt — Anrede',
  contactFirstName: 'Kontakt — Vorname',
  contactLastName: 'Kontakt — Nachname',
  contactTitle: 'Kontakt — Titel',
  contactDepartment: 'Kontakt — Abteilung',
  contactPosition: 'Kontakt — Position',
  contactWebsite: 'Kontakt — Webseite',
  contactNotes: 'Kontakt — Notizen',
  // Contact Details
  contactEMail: 'Kontakt — E-Mail',
  contactEMail2: 'Kontakt — E-Mail 2',
  contactEMail3: 'Kontakt — E-Mail 3',
  contactPhone: 'Kontakt — Telefon',
  contactPhone2: 'Kontakt — Telefon 2',
  contactPhone3: 'Kontakt — Telefon 3',
  contactCellphone: 'Kontakt — Mobil',
  contactCellphone2: 'Kontakt — Mobil 2',
  contactCellphone3: 'Kontakt — Mobil 3',
  contactFax: 'Kontakt — Fax',
  contactStreet: 'Kontakt — Straße',
  contactZip: 'Kontakt — PLZ',
  contactCity: 'Kontakt — Ort',
  contactCountry: 'Kontakt — Land',
  contactRegion: 'Kontakt — Region',
};

const csvMappingOptions = Object.entries(CSV_MAPPING_LABELS).map(
  ([key, label]) => ({ value: key, label })
);

// Auto-mapping: CSV header aliases → mapping field key
// Each key is a lowercase alias, value is the mapping field
const AUTO_MAP_ALIASES: Record<string, string> = {
  // Contact — Salutation
  anrede: 'contactSalutation',
  salutation: 'contactSalutation',
  briefanrede: 'contactSalutation',
  greeting: 'contactSalutation',
  // Contact — First Name
  vorname: 'contactFirstName',
  'first name': 'contactFirstName',
  firstname: 'contactFirstName',
  givenname: 'contactFirstName',
  'given name': 'contactFirstName',
  // Contact — Last Name
  nachname: 'contactLastName',
  'last name': 'contactLastName',
  lastname: 'contactLastName',
  familienname: 'contactLastName',
  surname: 'contactLastName',
  name: 'contactLastName',
  // Contact — Title
  titel: 'contactTitle',
  title: 'contactTitle',
  'akad. grad': 'contactTitle',
  'akademischer grad': 'contactTitle',
  'academic title': 'contactTitle',
  // Contact — Department
  abteilung: 'contactDepartment',
  department: 'contactDepartment',
  bereich: 'contactDepartment',
  organisationseinheit: 'contactDepartment',
  // Contact — Position
  position: 'contactPosition',
  funktion: 'contactPosition',
  rolle: 'contactPosition',
  'job title': 'contactPosition',
  jobtitle: 'contactPosition',
  // Contact — Website
  webseite: 'contactWebsite',
  website: 'contactWebsite',
  homepage: 'contactWebsite',
  url: 'contactWebsite',
  internetadresse: 'contactWebsite',
  // Contact — Notes
  notizen: 'contactNotes',
  notes: 'contactNotes',
  bemerkungen: 'contactNotes',
  anmerkungen: 'contactNotes',
  kommentar: 'contactNotes',
  comments: 'contactNotes',
  // Contact — Email
  'e-mail': 'contactEMail',
  email: 'contactEMail',
  mail: 'contactEMail',
  'e-mail-adresse': 'contactEMail',
  'email address': 'contactEMail',
  'email-adresse': 'contactEMail',
  'e-mail 2': 'contactEMail2',
  'email 2': 'contactEMail2',
  'weitere e-mail': 'contactEMail2',
  'second email': 'contactEMail2',
  'e-mail 3': 'contactEMail3',
  'email 3': 'contactEMail3',
  // Contact — Phone
  telefon: 'contactPhone',
  phone: 'contactPhone',
  'tel.': 'contactPhone',
  'tel. geschäftl.': 'contactPhone',
  'business phone': 'contactPhone',
  'telefon geschäftlich': 'contactPhone',
  'telefon 2': 'contactPhone2',
  'phone 2': 'contactPhone2',
  'telefon 3': 'contactPhone3',
  'phone 3': 'contactPhone3',
  // Contact — Cellphone
  mobil: 'contactCellphone',
  cellphone: 'contactCellphone',
  mobile: 'contactCellphone',
  handy: 'contactCellphone',
  mobiltelefon: 'contactCellphone',
  'mobile phone': 'contactCellphone',
  mobilnummer: 'contactCellphone',
  'mobil 2': 'contactCellphone2',
  'mobil 3': 'contactCellphone3',
  // Contact — Fax
  fax: 'contactFax',
  faxnummer: 'contactFax',
  'fax number': 'contactFax',
  telefax: 'contactFax',
  // Contact — Address
  strasse: 'contactStreet',
  straße: 'contactStreet',
  street: 'contactStreet',
  'straße privat': 'contactStreet',
  adresse: 'contactStreet',
  address: 'contactStreet',
  plz: 'contactZip',
  zip: 'contactZip',
  postleitzahl: 'contactZip',
  'postal code': 'contactZip',
  ort: 'contactCity',
  stadt: 'contactCity',
  city: 'contactCity',
  wohnort: 'contactCity',
  land: 'contactCountry',
  country: 'contactCountry',
  'länderkennz.': 'contactCountry',
  länderkennzeichen: 'contactCountry',
  region: 'contactRegion',
  bundesland: 'contactRegion',
  state: 'contactRegion',
  // Company
  firmenname: 'companyLegalName',
  firma: 'companyLegalName',
  unternehmen: 'companyLegalName',
  'company name': 'companyLegalName',
  company: 'companyLegalName',
  organisation: 'companyLegalName',
  'firmen-kurzname': 'companyDisplayName',
  'firmenabk.': 'companyDisplayName',
  firmenabkürzung: 'companyDisplayName',
  kurzname: 'companyDisplayName',
  'short name': 'companyDisplayName',
  'firmen-notizen': 'companyNotes',
  handelsregisternummer: 'companyCommercialRegisterNumber',
  'hr-nummer': 'companyCommercialRegisterNumber',
  handelsregisterstadt: 'companyCommercialRegisterCity',
  rechtsform: 'companyLegalForm',
  'legal form': 'companyLegalForm',
  'ust-idnr': 'companyVatNumber',
  'ust-id': 'companyVatNumber',
  'ust-idnr.': 'companyVatNumber',
  vatnumber: 'companyVatNumber',
  steuernummer: 'companyVatNumber',
  // Bank
  iban: 'bankIban',
  'iban-nr': 'bankIban',
  'iban-nr.': 'bankIban',
  bic: 'bankBic',
  'swift/bic': 'bankBic',
  swift: 'bankBic',
  kreditinstitut: 'bankCreditInstitutionName',
  bank: 'bankCreditInstitutionName',
  bankname: 'bankCreditInstitutionName',
  'name der bank': 'bankCreditInstitutionName',
  // BusinessUnit
  standort: 'businessUnitDisplayName',
  'standort-name': 'businessUnitDisplayName',
  niederlassung: 'businessUnitDisplayName',
  filiale: 'businessUnitDisplayName',
  'büro/filiale': 'businessUnitDisplayName',
  office: 'businessUnitDisplayName',
  branch: 'businessUnitDisplayName',
  'standort-telefon': 'businessUnitPhone',
  zentrale: 'businessUnitPhone',
  'office phone': 'businessUnitPhone',
  'standort-e-mail': 'businessUnitEMail',
  'standort-email': 'businessUnitEMail',
  'office mail': 'businessUnitEMail',
  'office email': 'businessUnitEMail',
  'standort-strasse': 'businessUnitStreet',
  'standort-straße': 'businessUnitStreet',
  firmenadresse: 'businessUnitStreet',
  firmenstraße: 'businessUnitStreet',
  'company address': 'businessUnitStreet',
  'standort-plz': 'businessUnitZip',
  'firmen-plz': 'businessUnitZip',
  'standort-ort': 'businessUnitCity',
  firmenort: 'businessUnitCity',
  'standort-land': 'businessUnitCountry',
  firmenland: 'businessUnitCountry',
  'standort-region': 'businessUnitRegion',
};

/**
 * Auto-map CSV column headers to mapping fields.
 * Returns a map of { mappingField: csvColumnHeader }
 */
function autoMapColumns(csvHeaders: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  const usedFields = new Set<string>();

  for (const header of csvHeaders) {
    const normalized = header.trim().toLowerCase();
    const matchedField = AUTO_MAP_ALIASES[normalized];
    if (matchedField && !usedFields.has(matchedField)) {
      result[matchedField] = header;
      usedFields.add(matchedField);
    }
  }

  return result;
}

const NewImportDrawer: React.FC<NewImportDrawerProps> = ({
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const classes = useStyles();

  const [columns, setColumns] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{
    [key: string]: string;
  }>({});
  const [nameSimilarityThreshold, setNameSimilarityThreshold] =
    useState<number>(0.75);
  const [maxLevenshteinDistance, setMaxLevenshteinDistance] =
    useState<number>(3);
  // const [useAiNormalization, setUseAiNormalization] = useState(false);
  const [canStartImport, setCanStartImport] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileUpload = (uploadedFile: any) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text || !text.trim()) return;

      // Get first line only for header detection
      const firstLine = text.split(/\r?\n/)[0] || '';

      // Detect separator: count occurrences in first line
      // Semicolons are preferred for German CSVs
      const semicolonCount = (firstLine.match(/;/g) || []).length;
      const commaCount = (firstLine.match(/,/g) || []).length;
      const separator = semicolonCount >= commaCount ? ';' : ',';

      // Parse headers: split, trim whitespace, remove BOM and quotes
      const headers = firstLine
        .split(separator)
        .map((h) =>
          h
            .trim()
            .replace(/^\uFEFF/, '')
            .replace(/^["']|["']$/g, '')
        )
        .filter((h) => h.length > 0);

      setColumns(headers);

      // Auto-map CSV headers to mapping fields
      const autoMapped = autoMapColumns(headers);
      setSelectedOptions(autoMapped);
    };
    // Explicit UTF-8 encoding for proper Umlaut/special char handling
    reader.readAsText(uploadedFile, 'UTF-8');
    setFile(uploadedFile);
    setCanStartImport(true);
    return false;
  };

  const handleSelectChange = (column: string, value: string) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [value]: column,
    }));
  };

  const buildMappingData = (): CsvMappingDto => {
    // selectedOptions is { mappingFieldKey: csvColumnHeader }
    const get = (key: string) => selectedOptions[key] || undefined;
    return {
      nameSimilarityThreshold,
      maxLevenshteinDistance,
      useAiNormalization: false, // TODO: re-enable when backend is ready
      companyLegalName: get('companyLegalName'),
      companyDisplayName: get('companyDisplayName'),
      companyNotes: get('companyNotes'),
      companyCommercialRegisterNumber: get('companyCommercialRegisterNumber'),
      companyCommercialRegisterCity: get('companyCommercialRegisterCity'),
      companyLegalForm: get('companyLegalForm'),
      companyVatNumber: get('companyVatNumber'),
      bankCreditInstitutionName: get('bankCreditInstitutionName'),
      bankBic: get('bankBic'),
      bankIban: get('bankIban'),
      businessUnitDisplayName: get('businessUnitDisplayName'),
      businessUnitPhone: get('businessUnitPhone'),
      businessUnitEMail: get('businessUnitEMail'),
      businessUnitStreet: get('businessUnitStreet'),
      businessUnitZip: get('businessUnitZip'),
      businessUnitCity: get('businessUnitCity'),
      businessUnitCountry: get('businessUnitCountry'),
      businessUnitRegion: get('businessUnitRegion'),
      businessUnitIsPrimary: get('businessUnitIsPrimary'),
      contactSalutation: get('contactSalutation'),
      contactFirstName: get('contactFirstName'),
      contactLastName: get('contactLastName'),
      contactTitle: get('contactTitle'),
      contactDepartment: get('contactDepartment'),
      contactPosition: get('contactPosition'),
      contactWebsite: get('contactWebsite'),
      contactNotes: get('contactNotes'),
      contactEMail: get('contactEMail'),
      contactEMail2: get('contactEMail2'),
      contactEMail3: get('contactEMail3'),
      contactPhone: get('contactPhone'),
      contactPhone2: get('contactPhone2'),
      contactPhone3: get('contactPhone3'),
      contactCellphone: get('contactCellphone'),
      contactCellphone2: get('contactCellphone2'),
      contactCellphone3: get('contactCellphone3'),
      contactFax: get('contactFax'),
      contactStreet: get('contactStreet'),
      contactZip: get('contactZip'),
      contactCity: get('contactCity'),
      contactCountry: get('contactCountry'),
      contactRegion: get('contactRegion'),
    };
  };

  const handleUpload = async () => {
    if (!file || Object.keys(selectedOptions).length === 0) {
      return;
    }

    const mappingData = buildMappingData();

    try {
      setIsSaving(true);
      setUploadError(null);
      const { result, data: processId } = await apiUploadCsvWithMapping(
        file,
        mappingData
      );
      if (result.ok) {
        onClose();
        if (processId) {
          navigate(`edit/${processId}`);
        }
      } else {
        let errorMsg = `Upload fehlgeschlagen (${result.status})`;
        try {
          const errorBody = await result.text();
          if (errorBody) errorMsg = errorBody;
        } catch {}
        setUploadError(errorMsg);
      }
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'Unbekannter Fehler'
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Find which mapping field is assigned to a CSV column
  const getMappingForColumn = (csvHeader: string): string | undefined => {
    return Object.entries(selectedOptions).find(
      ([, header]) => header === csvHeader
    )?.[0];
  };

  const getFilteredOptions = (column: string) => {
    const usedMappingFields = new Set(Object.keys(selectedOptions));
    const currentMapping = getMappingForColumn(column);
    return csvMappingOptions.filter(
      (option) =>
        !usedMappingFields.has(option.value) || option.value === currentMapping
    );
  };

  return (
    <Drawer
      title={t('settings:companyImport.newDrawerTitle')}
      visible={visible}
      onClose={onClose}
      width={'large'}
      className={classes.drawer}
    >
      <Upload
        beforeUpload={handleFileUpload}
        accept=".csv"
        showUploadList={false}
      >
        <Button
          iconProp={['fal', 'file-upload']}
          className={classes.uploadButton}
          disabled={isSaving}
        >
          {t('settings:companyImport.uploadCSV')}
        </Button>
      </Upload>

      {columns.length > 0 && (
        <div className={classes.selectContainer}>
          {columns.map((column, index) => (
            <div key={index} style={{ marginBottom: '16px' }}>
              <label>{column}</label>
              <Select
                options={getFilteredOptions(column)}
                placeholder={t('settings:companyImport.selectColumns')}
                style={{ width: '100%' }}
                onChange={(value) => handleSelectChange(column, value)}
                value={getMappingForColumn(column)}
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              />
            </div>
          ))}
        </div>
      )}

      <div className={classes.inputContainer}>
        <Tooltip
          title={t('settings:companyImport.nameSimilarityThresholdExplanation')}
        >
          <label>{t('settings:companyImport.nameSimilarityThreshold')}</label>
          <Input
            type="number"
            value={nameSimilarityThreshold}
            onChange={(e) => setNameSimilarityThreshold(Number(e))}
            placeholder={t(
              'settings:companyImport.nameSimilarityThresholdPlaceholder'
            )}
            style={{ width: '100%' }}
          />
        </Tooltip>
      </div>

      <div className={classes.inputContainer}>
        <Tooltip
          title={t('settings:companyImport.maxLevenshteinDistanceExplanation')}
        >
          <label>{t('settings:companyImport.maxLevenshteinDistance')}</label>
          <Input
            type="number"
            value={maxLevenshteinDistance}
            onChange={(e) => setMaxLevenshteinDistance(Number(e))}
            placeholder={t(
              'settings:companyImport.maxLevenshteinDistancePlaceholder'
            )}
            style={{ width: '100%' }}
          />
        </Tooltip>
      </div>

      {/* TODO: KI-Normalisierung — Backend noch nicht fertig
      <div className={classes.switchContainer}>
        <Tooltip
          title={t('settings:companyImport.useAiNormalizationTooltip')}
        >
          <Switch
            checked={useAiNormalization}
            onChange={setUseAiNormalization}
          />
        </Tooltip>
        <label>{t('settings:companyImport.useAiNormalization')}</label>
      </div>
      */}

      {uploadError && (
        <div
          style={{
            padding: '8px 16px',
            marginBottom: 16,
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 4,
            color: '#cf1322',
          }}
        >
          {uploadError}
        </div>
      )}

      <div className={classes.buttonContainer}>
        <Button disabled={isSaving} onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          disabled={isSaving || !canStartImport}
          type="primary"
          onClick={handleUpload}
          className={classes.actionButton}
        >
          {t('settings:companyImport.upload')}
        </Button>
      </div>
    </Drawer>
  );
};

export default NewImportDrawer;
